// Old URLs of the tabbed customer page and the top navigation (before the
// portal workbench, 2026-09-29), and where each goes now. Every redirect
// keeps the customer and, when the user cannot see the target, applies the
// landing rule (lib/navigation.js sectionTarget). The route elements in
// pages/LegacyRedirect.jsx navigate with replace, so the old URL never stays
// in the history.

import { customerSectionPath, sectionTarget, validId } from './navigation.js';

/** Old tab id to new customer section id (the qlik tab came with ENTRAID-282). */
export const LEGACY_TABS = Object.freeze({
  overview: 'overview',
  security: 'security',
  runs: 'data-collection',
  settings: 'data-collection',
  qlik: 'qlik-cloud',
  notifications: 'notifications',
  people: 'people',
});

/**
 * Section routes renamed since: Collection to Sources (2026-09-30), then Sources and Collections to Data collection
 * (ENTRAID-347 redesign, plan C5). The 2026-09-30 alias "activity" (the old Collections) ended when Activity became its
 * own section (plan C3): /activity is the Watch events page now.
 */
export const LEGACY_SECTIONS = Object.freeze({
  collection: 'data-collection',
  collections: 'data-collection',
  sources: 'data-collection',
});

/** Old names of the source toggles: their redirect lands on the Sources part of Data collection. */
const SOURCES_ALIASES = new Set(['settings', 'collection', 'sources']);
const SOURCES_HASH = '#sources';

/** Adds the Sources anchor when an old sources URL lands on Data collection itself. */
function toSources(to, customerId, old) {
  return SOURCES_ALIASES.has(old) && to === customerSectionPath(customerId, 'data-collection') ? `${to}${SOURCES_HASH}` : to;
}

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** /customers/:id, with or without its old tab parameter. */
export function legacyCustomerTarget(claims, customerId, search = '') {
  const tab = new URLSearchParams(search).get('tab') ?? '';
  const section = hasOwn(LEGACY_TABS, tab) ? LEGACY_TABS[tab] : 'overview';
  return toSources(sectionTarget(claims, customerId, section), customerId, tab);
}

/** /customers/:id/<old section>: the section routes before a rename (LEGACY_SECTIONS). */
export function legacySectionTarget(claims, customerId, oldSection) {
  const to = sectionTarget(claims, customerId, hasOwn(LEGACY_SECTIONS, oldSection) ? LEGACY_SECTIONS[oldSection] : 'overview');
  return toSources(to, customerId, oldSection);
}

// Tabs by role, mirroring ComplianceSection (display only; the API enforces roles). A tab the caller cannot see falls
// back to the section without a tab.
const TABS_ADMIN = Object.freeze(['reports', 'controls', 'attestations', 'schedule']);
const TABS_AUDITOR = Object.freeze(['reports', 'controls', 'attestations']);
const TABS_OPERATOR = Object.freeze(['reports']);

/** /customers/:id/compliance[?tab=]: the section before the ENTRAID-341 rename. Keeps a known tab; the landing rule applies. */
export function legacyComplianceTarget(claims, customerId, search = '') {
  const to = sectionTarget(claims, customerId, 'reports');
  if (to !== customerSectionPath(customerId, 'reports')) return to;
  const tabs = claims?.admin === true && claims?.customerId === customerId ? TABS_ADMIN : claims?.auditor === true ? TABS_AUDITOR : TABS_OPERATOR;
  const tab = new URLSearchParams(search).get('tab') ?? '';
  return tabs.includes(tab) ? `${to}?tab=${tab}` : to;
}

/** /settings: the Sources part of the active customer's Data collection (the old SettingsRedirect). */
export function legacySettingsTarget(claims) {
  if (validId(claims?.customerId)) return toSources(sectionTarget(claims, claims.customerId, 'data-collection'), claims.customerId, 'settings');
  if (claims?.isOperator === true) return '/customers';
  return '/';
}

/**
 * /data-reference: the generic catalog for an operator (null: render it
 * here), the customer route for an operator naming ?customerId=, and a
 * customer user's own reference (the parameter is ignored for them).
 */
export function dataReferenceTarget(claims, search = '', hash = '') {
  if (claims?.isOperator === true) {
    const named = new URLSearchParams(search).get('customerId');
    return validId(named) ? withHash(sectionTarget(claims, named, 'data-reference'), named, hash) : null;
  }
  if (validId(claims?.customerId)) {
    return withHash(sectionTarget(claims, claims.customerId, 'data-reference'), claims.customerId, hash);
  }
  return '/';
}

// Keep an anchor (#source-<key>) only when the target is the reference itself.
function withHash(to, customerId, hash) {
  return hash && to === customerSectionPath(customerId, 'data-reference') ? `${to}${hash}` : to;
}

/**
 * /customers/:id/company-settings and /customers/:id/deployment, with their old tab, after both became Settings
 * (ENTRAID-347 plan C6):
 *   company-settings, or ?tab=branding        -> settings/company
 *   company-settings?tab=email|integrations    -> notifications/delivery (moved there in C4)
 *   deployment, or ?tab=deployment             -> settings/aws-deployment
 *   deployment?tab=account                     -> settings/microsoft-connection (the consent card)
 *   deployment?tab=danger                      -> settings/danger-zone for an operator, else settings/aws-deployment
 * A viewer who cannot see the target section gets the landing rule.
 */
export function legacySettingsSectionTarget(claims, customerId, from, search = '') {
  const tab = new URLSearchParams(search).get('tab') ?? '';
  if (from === 'company-settings' && (tab === 'email' || tab === 'integrations')) {
    const to = sectionTarget(claims, customerId, 'notifications');
    return to === `${customerSectionPath(customerId, 'notifications')}/rules` ? `${customerSectionPath(customerId, 'notifications')}/delivery` : to;
  }
  const to = sectionTarget(claims, customerId, 'settings');
  if (to !== customerSectionPath(customerId, 'settings')) return to;
  if (from !== 'deployment') return `${to}/company`;
  if (tab === 'account') return `${to}/microsoft-connection`;
  if (tab === 'danger' && claims?.isOperator === true) return `${to}/danger-zone`;
  return `${to}/aws-deployment`;
}
